import React, { useEffect, useRef, useState } from "react"
import styled, { keyframes } from "styled-components"
import gsap, { CustomEase } from "gsap/all"

import colors from "assets/styles/colors"
import text, { responsiveText } from "assets/styles/text"
import media from "assets/styles/media"

import BGGrid from "components/uIElements/BGGrid"
import { useMedia } from "utils/hooks"
import LoadingWindowSVG from "assets/images/homepage/Years-LoadingWindow.svg"
import LoadingBarsSVG from "assets/images/homepage/Years-LoadingBars.svg"

type FigureRef = HTMLElement & {
  children: [
    {
      style: {
        transform: string
      }
    }
  ]
}

const Years: React.FC<{}> = () => {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const figureRef = useRef<FigureRef>(null)
  const translateValue = useMedia(100, 100, 100, 40)

  useEffect(() => {
    const results: gsap.core.Tween[] = []

    //rotate figure
    if (figureRef.current) {
      results.push(
        gsap.to(figureRef.current.children[1], {
          ease: "power2.inOut",
          rotate: 180,
          scrollTrigger: {
            trigger: wrapper,
            scrub: true,
            scroller: "#___gatsby",
            start: "top 75%",
            end: "bottom top",
          },
        })
      )
    }

    //slide in text
    if (textRef.current && wrapper) {
      results.push(
        gsap.to(textRef.current.children, {
          transform: `translate(0, 0)`,
          ease: "power2",
          opacity: 1,
          duration: 10,
          scrollTrigger: {
            trigger: wrapper,
            scrub: true,
            scroller: "#___gatsby",
            start: "top 60%",
            end: "bottom 40%",
          },
        })
      )
    }

    //sticky scroll
    if (wrapper) {
      results.push(
        gsap.fromTo(
          wrapper.children,
          {
            transform: "translateY(0vh)",
          },
          {
            transform: `translateY(${translateValue}vh)`,
            ease: CustomEase.create("custom", "M0, 0 C0.25, 0 0.75, 1 1, 1 "),
            opacity: 1,
            scrollTrigger: {
              trigger: wrapper,
              scrub: true,
              scroller: "#___gatsby",
              start: "top 20%",
              end: "bottom 80%",
            },
          }
        )
      )
    }

    return () => {
      results.forEach(tween => {
        tween.kill()
      })
    }
  }, [wrapper])

  const gridConfig = {
    sectionHeight: "100%",
    sectionRef: wrapper,
    animationClass: "years",
  }

  return (
    <Wrapper
      id="Years"
      ref={ref => setWrapper(ref)}
      data-scroll
      data-scroll-section
    >
      <BGGrid lineConfig={gridConfig} />
      <InnerWrapper>
        <TextWrapper ref={textRef}>
          <TextLine0>a lot has</TextLine0>
          <TextLine1>happened</TextLine1>
          <TextLine2>in the last</TextLine2>
          <TextLine3>28 years...</TextLine3>
        </TextWrapper>
        <Figure ref={figureRef}>
          <LoadingWindow src={LoadingWindowSVG} alt={"retro loading window"} />
          <LoadingBars src={LoadingBarsSVG} alt={"loading bars"} />
          <LoadingText>
            <Line>LOADING...</Line>
            <Line>LOADING...</Line>
            <Line>LOADING...</Line>
          </LoadingText>
        </Figure>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  background: ${colors.blue400};
  padding: 0 10.3vw;
  position: relative;
  height: calc(200vh + 4.167vw);
  grid-template-rows: auto 1fr;
  place-items: center;

  ${media.tablet} {
    grid-template-columns: 1fr;
    grid-template-rows: 50vh 50vh;
    gap: 10px;
    height: calc(200vh + 7.194vw);
  }

  ${media.mobile} {
    height: 150vh;
    grid-template-columns: 1fr;
    grid-template-rows: 50vh 50vh;
    padding: 2.67vw;
    gap: 2.67vw;
  }
`

const InnerWrapper = styled.div`
  height: 90vh;
  margin-top: 10vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4.167vw;
  position: relative;
  z-index: 3;
  ${media.tablet} {
    flex-direction: column;
    gap: 5.995vw;
  }
  ${media.mobile} {
    flex-direction: column;
    gap: 13.333vw;
  }
`

const TextWrapper = styled.div`
  position: relative;
  ${media.tablet} {
    align-self: center;
  }
  ${media.mobile} {
    align-self: center;
  }
`

const TextLine = styled.h2`
  ${responsiveText.h3}
  color: ${colors.white};
  opacity: 0;
  ${media.mobile} {
    ${text.mobile.h4};
  }
`

const TextLine0 = styled(TextLine)`
  transform: translate(-20.83vw, 20.56vw);
`

const TextLine1 = styled(TextLine)`
  transform: translate(-13.89vw, 13.61vw);
`

const TextLine2 = styled(TextLine)`
  transform: translate(-6.94vw, 6.67vw);
`

const TextLine3 = styled(TextLine)`
  transform: translate(-3.47vw, 3.19vw);
`

const Figure = styled.figure`
  width: 37.92vw;
  position: relative;

  ${media.tablet} {
    width: 72.949vw;
  }

  ${media.mobile} {
    width: 92vw;
  }
`

const LoadingWindow = styled.img`
  box-shadow: 0px 40px 80px rgba(0, 0, 0, 0.25);
  width: 100%;
`

const LoadingBars = styled.img`
  width: 16.667vw;
  height: 16.667vw;
  margin: 0;
  position: absolute;
  top: calc(55% - 8.335vw);
  left: calc(50% - 8.335vw);

  ${media.tablet} {
    width: 32.129vw;
    height: 32.129vw;
    top: calc(55% - 16.065vw);
    left: calc(50% - 16.065vw);
  }

  ${media.mobile} {
    width: 40.53vw;
    height: 40.53vw;
    top: calc(55% - 20.267vw);
    left: calc(50% - 20.267vw);
  }
`

const LoadingText = styled.div`
  margin: 0;
  width: 4.48vw;
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${media.tablet} {
    width: 9.766vw;
    left: 52.5%;
  }
  ${media.mobile} {
    width: 18.13vw;
    left: 55%;
  }
`

const LineAnim = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`

const Line = styled.p`
  color: transparent;
  font: 1.3vw "NeueBit";
  background: linear-gradient(
    90deg,
    ${colors.black800} 10%,
    ${colors.white},
    ${colors.black800} 90%
  );
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  animation: ${LineAnim} 3s infinite normal linear;
  &:first-of-type {
    margin-bottom: 0.75vw;
    animation-duration: 2.5s;
  }
  &:nth-of-type(2) {
    margin-bottom: 0.75vw;
    background-position: 91% 100%;
    animation-delay: 1s;
  }
  &:last-of-type {
    animation-delay: 2s;
    animation-duration: 3.5s;
  }
  ${media.tablet} {
    font-size: 2.172vw;
  }

  ${media.mobile} {
    font-size: 3vw;
    &:first-of-type {
      margin-bottom: 3.07vw;
    }
    &:nth-of-type(2) {
      margin-bottom: 3.07vw;
    }
  }
`

export default Years
